import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [nativeLanguage, setNativeLanguage] = useState("");
  const [learningLanguage, setLearningLanguage] = useState("");
  const [bio, setBio] = useState("");
  const [languages, setLanguages] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/languages").then(({ data }) => setLanguages(data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!nativeLanguage) { setErr("Please pick your native language."); return; }
    if (!learningLanguage) { setErr("Please pick the language you're learning."); return; }
    if (nativeLanguage === learningLanguage) {
      setErr("Learning language should be different from your native one.");
      return;
    }
    setLoading(true);
    try {
      await register({
        name, email, password, bio,
        native_language: nativeLanguage,
        learning_language: learningLanguage,
      });
      toast.success(`Welcome, ${name.split(" ")[0]}.`);
      nav("/today");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-shell grain flex flex-col p-6">
      <header className="pt-10">
        <Link to="/welcome" className="text-xs tracking-[0.3em] uppercase text-muted-foreground" data-testid="back-home">← Back</Link>
      </header>
      <div className="flex-1 flex flex-col justify-center animate-fade-up py-8">
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">Say hello</h1>
        <p className="mt-3 text-muted-foreground">
          Tell us who you are and which language you'd like to swap.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="register-form">
          <div className="space-y-2">
            <Label className="text-xs tracking-wide uppercase text-muted-foreground">First name</Label>
            <Input required value={name} onChange={(e) => setName(e.target.value)} className="h-12 rounded-2xl bg-card" data-testid="register-name" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs tracking-wide uppercase text-muted-foreground">Email</Label>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 rounded-2xl bg-card" data-testid="register-email" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs tracking-wide uppercase text-muted-foreground">Password</Label>
            <Input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 rounded-2xl bg-card" data-testid="register-password" />
          </div>
          <div className="grid grid-cols-1 gap-5">
            <div className="space-y-2">
              <Label className="text-xs tracking-wide uppercase text-muted-foreground">You speak (native)</Label>
              <Select value={nativeLanguage} onValueChange={setNativeLanguage}>
                <SelectTrigger className="h-12 rounded-2xl bg-card" data-testid="register-native-language">
                  <SelectValue placeholder="Your native language" />
                </SelectTrigger>
                <SelectContent>
                  {languages.map((l) => (
                    <SelectItem key={l} value={l} data-testid={`native-option-${l}`}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-wide uppercase text-muted-foreground">You're learning</Label>
              <Select value={learningLanguage} onValueChange={setLearningLanguage}>
                <SelectTrigger className="h-12 rounded-2xl bg-card" data-testid="register-learning-language">
                  <SelectValue placeholder="Language you want to practice" />
                </SelectTrigger>
                <SelectContent>
                  {languages.map((l) => (
                    <SelectItem key={l} value={l} data-testid={`learning-option-${l}`}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs tracking-wide uppercase text-muted-foreground">
              A line about you <span className="normal-case text-muted-foreground/70">(optional)</span>
            </Label>
            <Textarea value={bio} onChange={(e) => setBio(e.target.value)} className="rounded-2xl bg-card min-h-[80px]" maxLength={240} data-testid="register-bio" />
          </div>
          {err && <p className="text-sm text-destructive" data-testid="register-error">{err}</p>}
          <Button type="submit" disabled={loading} className="w-full h-14 rounded-full text-base bg-primary hover:bg-primary/90 transition-transform hover:-translate-y-0.5" data-testid="register-submit">
            {loading ? "Creating…" : "Lock me in"}
          </Button>
        </form>

        <p className="mt-6 text-sm text-center text-muted-foreground">
          Already have an account? <Link to="/login" className="text-primary underline underline-offset-4" data-testid="go-login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
